// Utilitários para SEO
export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  schema?: any;
}

export const updateMetaTags = (seo: SEOData) => {
  // Title
  document.title = seo.title;

  // Meta Description
  updateMetaTag('name', 'description', seo.description);

  // Keywords
  if (seo.keywords) {
    updateMetaTag('name', 'keywords', seo.keywords);
  }

  // Canonical
  if (seo.canonical) {
    updateLinkTag('canonical', seo.canonical);
  }

  // Open Graph
  updateMetaTag('property', 'og:title', seo.title);
  updateMetaTag('property', 'og:description', seo.description);
  if (seo.canonical) {
    updateMetaTag('property', 'og:url', seo.canonical);
  }
  if (seo.ogImage) {
    updateMetaTag('property', 'og:image', seo.ogImage);
  }
  if (seo.ogType) {
    updateMetaTag('property', 'og:type', seo.ogType);
  }

  // Twitter Cards
  updateMetaTag('name', 'twitter:title', seo.title);
  updateMetaTag('name', 'twitter:description', seo.description);
  if (seo.ogImage) {
    updateMetaTag('name', 'twitter:image', seo.ogImage);
  }

  // Schema.org
  if (seo.schema) {
    updateSchema(seo.schema);
  }

  // Last Modified
  updateMetaTag('name', 'last-modified', new Date().toISOString().split('T')[0]);
};

const updateMetaTag = (attr: string, key: string, content: string) => {
  let element = document.querySelector(`meta[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.href = href;
};

const updateSchema = (schema: any) => {
  const scriptId = 'schema-org-json-ld';
  let element = document.getElementById(scriptId);
  
  if (!element) {
    element = document.createElement('script');
    element.id = scriptId;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  
  // Se for array de schemas, combinar todos
  if (Array.isArray(schema)) {
    element.textContent = JSON.stringify(schema);
  } else {
    element.textContent = JSON.stringify(schema);
  }
};

// Gerar slug a partir de texto
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Schema.org para imóveis
export const generatePropertySchema = (imovel: any) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: imovel.titulo,
    description: imovel.descricao || `${imovel.tipo} com ${imovel.quartos} quartos em ${imovel.bairro}, ${imovel.cidade}`,
    image: imovel.fotos?.map((f: any) => f.url) || [],
    offers: {
      '@type': 'Offer',
      price: imovel.preco,
      priceCurrency: 'BRL',
      availability: imovel.status === 'disponivel' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      url: `${siteUrl}/imovel/${imovel.slug}`,
    },
    brand: {
      '@type': 'Organization',
      name: 'Mateus Oliveira Imóveis',
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Quartos',
        value: imovel.quartos,
      },
      {
        '@type': 'PropertyValue',
        name: 'Banheiros',
        value: imovel.banheiros,
      },
      {
        '@type': 'PropertyValue',
        name: 'Vagas',
        value: imovel.vagas,
      },
      {
        '@type': 'PropertyValue',
        name: 'Área',
        value: `${imovel.area_total || imovel.area_construida}m²`,
      },
    ],
  };
};

// Schema.org para vídeos
export const generateVideoSchema = (video: any) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  const videoId = extractYouTubeId(video.youtube_url);
  
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.titulo,
    description: video.descricao || video.titulo,
    thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    uploadDate: video.created_at,
    contentUrl: video.youtube_url,
    embedUrl: `https://www.youtube.com/embed/${videoId}`,
  };
};

// Schema.org para lista de vídeos
export const generateVideoListSchema = (videos: any[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: videos.map((video, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: generateVideoSchema(video),
    })),
  };
};

// Schema.org para organização
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  
  return {
    '@context': 'https://schema.org',
    '@type': ['RealEstateAgent', 'Organization'],
    name: 'Mateus Oliveira Imóveis',
    description: 'Imóveis de médio e alto padrão em Aracaju e Barra dos Coqueiros - Sergipe',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    telephone: '+55-79-99988-1002',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Aracaju',
      addressRegion: 'SE',
      addressCountry: 'BR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -10.9472,
      longitude: -37.0731,
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Aracaju',
      },
      {
        '@type': 'City',
        name: 'Barra dos Coqueiros',
      },
    ],
    sameAs: [
      'https://www.instagram.com/mateusoliveiracorretor',
      'https://www.tiktok.com/@mateusoliveiracorretor',
      'https://www.youtube.com/@mateusoliveiracorretor',
    ],
  };
};

// Schema.org para breadcrumbs
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: siteUrl,
      },
      ...items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: item.name,
        item: `${siteUrl}${item.url}`,
      })),
    ],
  };
};

// Schema.org para página de contato
export const generateContactPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contato - Mateus Oliveira Imóveis',
    description: 'Entre em contato com a Mateus Oliveira Imóveis',
    url: `${siteUrl}/contato`,
    mainEntity: {
      '@type': 'RealEstateAgent',
      name: 'Mateus Oliveira Imóveis',
      telephone: '+55-79-99988-1002',
      email: 'contato@mateusimoveisse.com.br',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Aracaju',
        addressRegion: 'SE',
        addressCountry: 'BR',
      },
    },
  };
};

// Schema.org para página sobre
export const generateAboutPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Sobre - Mateus Oliveira Imóveis',
    description: 'Conheça a história e os valores da Mateus Oliveira Imóveis',
    url: `${siteUrl}/sobre`,
    mainEntity: generateOrganizationSchema(),
  };
};

// Schema.org para serviço de avaliação
export const generateServiceSchema = (serviceName: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceName,
    description: description,
    provider: {
      '@type': 'RealEstateAgent',
      name: 'Mateus Oliveira Imóveis',
      telephone: '+55-79-99988-1002',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Aracaju',
        addressRegion: 'SE',
        addressCountry: 'BR',
      },
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Aracaju',
      },
      {
        '@type': 'City',
        name: 'Barra dos Coqueiros',
      },
    ],
    url: siteUrl,
  };
};

// Schema.org para lista de imóveis
export const generatePropertyListSchema = (imoveis: any[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: imoveis.slice(0, 10).map((imovel, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: generatePropertySchema(imovel),
    })),
  };
};

// Schema.org para FAQ
export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

// Extrair ID do YouTube
export const extractYouTubeId = (url: string): string => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : '';
};

// Gerar sitemap (para uso futuro)
export const generateSitemap = async () => {
  // Esta função será chamada pelo backend ou build process
  return {
    urls: [
      { loc: '/', priority: 1.0, changefreq: 'daily' },
      { loc: '/imoveis', priority: 0.9, changefreq: 'daily' },
      { loc: '/comprar-imovel-sergipe', priority: 0.9, changefreq: 'weekly' },
      { loc: '/vender-imovel-sergipe', priority: 0.9, changefreq: 'weekly' },
      { loc: '/avaliar-imovel', priority: 0.8, changefreq: 'weekly' },
      { loc: '/sobre', priority: 0.7, changefreq: 'monthly' },
      { loc: '/contato', priority: 0.8, changefreq: 'monthly' },
      { loc: '/videos', priority: 0.7, changefreq: 'weekly' },
    ],
  };
};
